import { CheckIcon } from '@heroicons/react/16/solid'
import { format, formatDistance, roundToNearestMinutes } from 'date-fns'
import { type FunctionComponent, useMemo } from 'react'
import { cn, Icn } from 'tailwind-ui'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'tailwind-v4/components/popover'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import {
  type EventableOutgoingEmailType,
  type InboxThreadEventEventableMessageType,
} from '@/state/entities/inbox/inbox.types'
import { useAppDispatch } from '@/state/helpers/useApp'
import { usePusherSubscription } from '@/utils/pusher'
import inboxApi, { dateParse } from '@/state/inbox/inbox.slice'
import { ThreadPopover } from './common'
import { MessageRow, emailBodyClasses } from './message-row'

const formatCleanTime = (date: string | Date | null = '') => {
  if (!date) return 'never'
  return `${formatDistance(dateParse(date), new Date())} ago`
}

const BlueCheckTime: FunctionComponent<{
  label: string
  date: string | Date | null
}> = ({ label, date }) => {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-sm">{`${label} at ${formatCleanTime(date)}`}</div>
      <Icn
        size="sm"
        className={cn({
          'text-stampede-500': date,
          'text-bg-muted': !date,
        })}
        icon={CheckIcon}
      />
    </div>
  )
}

const BlueCheckStack: FunctionComponent<{
  item: EventableOutgoingEmailType
}> = ({ item }) => {
  return (
    <div key={item?.id} className="space-y-1">
      <span className="font-semibold">
        {`${item?.to_name ?? 'Unknown'} <${item?.to_email}>`}
      </span>
      <BlueCheckTime date={item?.sent_at} label="Sent" />
      <BlueCheckTime date={item?.delivered_at} label="Delivered" />
      <BlueCheckTime date={item?.first_opened_at} label="First seen" />
    </div>
  )
}

const ThreadMessageBox: FunctionComponent<{
  event: InboxThreadEventEventableMessageType
  threadId: string
  eventId: string
}> = ({ event, threadId, eventId }) => {
  const dispatch = useAppDispatch()
  const hasSentToAll = useMemo(() => {
    if (event.outgoing_emails.length === 0) return false
    return (
      event.outgoing_emails.filter((item: any) => Boolean(item?.sent_at))
        .length === event.outgoing_emails.length
    )
  }, [event.outgoing_emails])

  const hasDeliveredToAll = useMemo(() => {
    if (event.outgoing_emails.length === 0) return false
    return (
      event.outgoing_emails.filter((item: any) => Boolean(item?.delivered_at))
        .length === event.outgoing_emails.length
    )
  }, [event.outgoing_emails])

  const hasSeenToAll = useMemo(() => {
    if (event.outgoing_emails.length === 0) return false
    return (
      event.outgoing_emails.filter((item: any) =>
        Boolean(item?.first_opened_at)
      ).length === event.outgoing_emails.length
    )
  }, [event.outgoing_emails])

  const hasSeenPartial = useMemo(() => {
    if (event.outgoing_emails.length === 0) return false
    return (
      event.outgoing_emails.filter((item: any) =>
        Boolean(item?.first_opened_at)
      ).length >= 1
    )
  }, [event.outgoing_emails])

  usePusherSubscription<{ id: string; thread_id: string }>(
    `inbox.thread.${threadId}.thread_event.${eventId}.updated`,
    ({ thread_id }) => {
      dispatch(
        inboxApi.util.invalidateTags([
          'counts',
          { type: 'thread_events', id: thread_id },
        ])
      )
    }
  )

  const recipients = useMemo(
    () =>
      event.outgoing_emails
        .map((item: any) => item?.to_email)
        .filter(Boolean)
        .join(', '),
    [event.outgoing_emails]
  )

  const status = (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Delivery status"
            className="flex items-center"
          />
        }
      >
        <Icn
          size="xs"
          icon={CheckIcon}
          className={`${hasSeenPartial ? 'text-stampede-500' : 'text-neutral-400'} z-10`}
        />
        {hasDeliveredToAll && hasSentToAll ? (
          <Icn
            size="xs"
            icon={CheckIcon}
            className={`${hasSeenToAll ? 'text-stampede-500' : 'text-neutral-400'} relative -left-1.5`}
          />
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2 text-xs">
        {event.outgoing_emails.map((item: any) => (
          <BlueCheckStack key={item?.id} item={item} />
        ))}
        {event.outgoing_emails.length === 0 && <span>Message sending…</span>}
      </PopoverContent>
    </Popover>
  )

  return (
    <MessageRow
      direction="outgoing"
      name={event.user_name}
      email={event.user_email}
      meta={recipients ? `To: ${recipients}` : undefined}
      time={format(
        roundToNearestMinutes(event.created_at, { nearestTo: 10 }),
        TimeFormats.FourHourApple
      )}
      status={status}
      info={<ThreadPopover event={event} />}
    >
      <div
        className={emailBodyClasses}
        dangerouslySetInnerHTML={{ __html: event.description }}
      />
    </MessageRow>
  )
}

export default ThreadMessageBox
